import { initDB, migrateSeaTimeToIndexedDB, migrateContractsToIndexedDB, migrateExpensesToIndexedDB } from './db.js';
import { checkExpiringCertificates } from './notifications.js';
import { renderDashboard, renderCertificates, initCertificates } from './certificates.js';
import { initSettings } from './settings.js';
import { initTheme } from './theme.js';
import { showNotification } from './utils.js';

// Cache the dynamic import so clicking between Sea-Time / Contracts /
// CV Builder repeatedly only ever fetches tools.js once per session.
let toolsModulePromise = null;
function loadToolsModule() {
    if (!toolsModulePromise) {
        toolsModulePromise = import('./tools.js').then(mod => {
            mod.initTools();
            return mod;
        });
    }
    return toolsModulePromise;
}

const TOOLS_TABS = new Set(['seafarer-tools', 'sea-time', 'contracts', 'cv-builder', 'expenses']);

export function switchTab(tabName) {
    document.querySelectorAll('.tab-btn').forEach(btn => {
        btn.classList.remove('active');
        if (btn.dataset.tab === tabName) btn.classList.add('active');
    });

    document.querySelectorAll('.tab-content').forEach(content => content.classList.remove('active'));

    const target = document.getElementById(tabName);
    if (target) target.classList.add('active');

    if (tabName === 'dashboard') {
        renderDashboard();
    } else if (tabName === 'certificates') {
        renderCertificates();
    } else if (TOOLS_TABS.has(tabName)) {
        // First visit to ANY Tools tab triggers the network fetch for
        // tools.js (visible as its own file in the Network/Sources
        // panel, only appearing once the tab is actually opened).
        loadToolsModule().then(mod => {
            if (tabName === 'sea-time') mod.renderSeaTime();
            else if (tabName === 'contracts') mod.renderContracts();
            else if (tabName === 'cv-builder') mod.renderCvBuilder();
            else if (tabName === 'expenses') mod.renderExpenses();
        });
    }
}

// Exposed globally because index.html has inline onclick="switchTab(...)"
// handlers, and license.js (a plain non-module script) may also need it.
window.switchTab = switchTab;

// FIXED: this wiring existed in the original monolithic app.js but was
// never carried over when switchTab() moved into this module — the
// top nav (Dashboard/Certificates/Tools/Add Cert) had no click handler
// at all after the split. Restored here, unconditional and outside
// init() so it's wired up as soon as this script runs, same as before.
document.querySelectorAll('.tab-btn').forEach(btn => {
    btn.addEventListener('click', () => switchTab(btn.dataset.tab));
});

async function init() {
    try {
        await initDB();
        await migrateSeaTimeToIndexedDB();
        await migrateContractsToIndexedDB();
        await migrateExpensesToIndexedDB();
        initCertificates();
        initSettings();
        initTheme();
        await renderDashboard();
        await checkExpiringCertificates();
        setInterval(checkExpiringCertificates, 24 * 60 * 60 * 1000);
        console.log('App initialized successfully');
    } catch (error) {
        console.error('Error initializing app:', error);
        showNotification('Error initializing app: ' + (error.message || error));
    }
}

// Register service worker
if ('serviceWorker' in navigator) {
    navigator.serviceWorker.register('sw.js')
        .then(() => console.log('Service Worker registered'))
        .catch(err => console.error('Service Worker registration failed:', err));
}

// license.js calls window.__bootApp() once the license is verified —
// init() is intentionally not called directly here.
window.__bootApp = init;

// Dev bypass: uncomment to skip the license check locally.
// init();
